import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';

import FormInput from '../../components/form/FormInput';
import FormSelect from '../../components/form/FormSelect';

import { cleanPhoneNumber } from '../../utils/common';
import { useUserDetails } from '../../store/slices/authSlice';
import {
  agentViewTemplatesGet,
  sendMetaTemplateMsg,
  setActiveMetaHelpDetails,
  useActiveMetaHelpDetails,
  whatsappMetaAddDetails,
  whatsappMetaEditDetails
} from '../../store/slices/tenantSlices/metaHelpDeskSlice';

const AddEditMetaNumberModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useUserDetails();
  const activeMetaHelpDetails = useActiveMetaHelpDetails();

  const [templateOptions, setTemplateOptions] = useState([]);

  const initialValues = {
    name: '',
    from: '',
    is_send_template: false,
    template_id: ''
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('name is required'),
    from: Yup.string().required('number is required'),
    template_id: Yup.mixed().when('is_send_template', {
      is: true,
      then: (s) => s.required('template is required'),
      otherwise: (s) => s.notRequired()
    })
  });

  const toggleSendTemplate = () => {
    setFieldValue('is_send_template', !values.is_send_template);
    setFieldValue('template_id', '');
  };

  // Sent only after the number is saved, so a failing template never keeps the ticket
  // from being created - the axios interceptor already surfaces the API error itself.
  const sendTemplate = async (to, templateId) => {
    try {
      const payload = {
        from: user?.to_meta_whatsapp_number,
        to: to,
        template_id: templateId
      };
      const response = await dispatch(sendMetaTemplateMsg(payload)).unwrap();
      if (response && response?.message) {
        toast.success(response?.message);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = {
          name: values?.name,
          ticketId: editRecord.ticketId,
          from: values?.from
        };
        const response = await dispatch(whatsappMetaEditDetails(payload)).unwrap();
        if (response && response?.message) {
          dispatch(setActiveMetaHelpDetails({ ...activeMetaHelpDetails, name: values?.name }));
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { name: values?.name, from: values?.from };
        const response = await dispatch(whatsappMetaAddDetails(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          if (values?.is_send_template) {
            await sendTemplate(values?.from, values?.template_id);
          }
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue,
    isSubmitting
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const fetchTemplateOptions = async () => {
    try {
      const response = await dispatch(agentViewTemplatesGet()).unwrap();
      const options = (response?.data || []).map((val) => ({
        label: val?.name,
        value: val?.id
      }));
      setTemplateOptions(options);
    } catch (error) {
      setTemplateOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (!isEdit) {
      fetchTemplateOptions();
    }
  }, [isEdit]);

  useEffect(() => {
    if (isEdit) {
      setValues({
        ...values,
        name: editRecord?.name,
        from: editRecord?.from ? cleanPhoneNumber(editRecord?.from) : ''
      });
    }
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="md" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? t('editName') : t('addNumber')}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="12" md="12">
              <FormInput
                name="name"
                label={t('name')}
                placeholder="Enter Name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="12" md="12">
              <FormInput
                name="from"
                label={t('number')}
                placeholder="Enter Number"
                value={values.from}
                onChange={(e) => {
                  const value = e.target.value;
                  const val = value.replace(/[^0-9*#]+/g, '');
                  setFieldValue('from', val);
                }}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            {!isEdit ? (
              <Col lg="12" md="12">
                <div className="d-flex flex-row align-items-center">
                  <div className="me-2">
                    <FormGroup check className="mb-0">
                      <Label check>
                        <Input
                          type="checkbox"
                          id="is_send_template"
                          checked={values.is_send_template}
                          onChange={() => toggleSendTemplate()}
                        />
                      </Label>
                    </FormGroup>
                  </div>
                  <div
                    className="text-primary fw-bold cursor-pointer"
                    onClick={() => toggleSendTemplate()}>
                    Send Template
                  </div>
                </div>
              </Col>
            ) : null}
            {!isEdit && values.is_send_template ? (
              <Col lg="12" md="12" className="mt-3">
                <FormSelect
                  name="template_id"
                  label="Template"
                  placeholder="Select Template"
                  value={values.template_id}
                  options={templateOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            ) : null}
          </Row>
        </form>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-center">
        <Button
          disabled={isSubmitting}
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          {t('cancel')}
        </Button>
        <Button
          form="crms"
          type="submit"
          disabled={isSubmitting}
          className="px-5 bg-primary border-primary">
          {isEdit ? t('update') : values.is_send_template ? 'Save & Send' : t('save')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditMetaNumberModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditMetaNumberModal;
